import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Linkedin, Mail, Building2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* SKI AXIS Mentors — cinematic dark/gold */

const MENTORS_STYLES = `
.skia-mentors *{box-sizing:border-box;}
.skia-mentors{
  --ink:#FAF7F0;--ink2:#F4EFE6;--ink3:#FFFFFF;
  --gold:#C8A84B;--gold2:#D9BC6A;
  --gold-line:rgba(200,168,75,0.32);--gold-subtle:rgba(200,168,75,0.08);
  --cream:#0D1B2A;--white:#0D1B2A;--mist:#3A4A5C;--stone:#7A8190;
  background:var(--ink);color:var(--cream);min-height:100vh;
  font-family:'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif;
}
.skia-mentors h1,.skia-mentors h2,.skia-mentors h3{font-family:'Cormorant Display',Georgia,serif;letter-spacing:-.02em;color:var(--white);}

.skia-mentors .m-hero{position:relative;padding:80px 32px 56px;overflow:hidden;}
.skia-mentors .m-hero-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(200,168,75,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(200,168,75,.025) 1px,transparent 1px);background-size:80px 80px;}
.skia-mentors .m-hero-glow{position:absolute;top:-30%;right:-10%;width:60%;height:140%;background:radial-gradient(ellipse at 70% 40%,rgba(200,168,75,.07) 0%,transparent 60%);}
.skia-mentors .m-hero-mono{position:absolute;bottom:-10%;left:-3%;font-family:'Cormorant Display',serif;font-size:clamp(220px,26vw,360px);font-weight:700;font-style:italic;color:rgba(200,168,75,.04);line-height:.85;white-space:nowrap;letter-spacing:-.06em;user-select:none;pointer-events:none;}
.skia-mentors .m-hero-inner{position:relative;z-index:5;max-width:1320px;margin:0 auto;}
.skia-mentors .m-eyebrow{font-size:11px;font-weight:700;letter-spacing:.3em;text-transform:uppercase;color:var(--gold);margin-bottom:20px;display:flex;align-items:center;gap:12px;}
.skia-mentors .m-eyebrow::before{content:'';display:block;width:32px;height:1px;background:var(--gold);opacity:.6;}
.skia-mentors .m-h1{font-size:clamp(40px,5.6vw,72px);font-weight:700;line-height:1;margin-bottom:18px;}
.skia-mentors .m-h1 em{font-style:italic;color:var(--gold);}
.skia-mentors .m-sub{font-family:'Cormorant Display',serif;font-size:clamp(17px,1.9vw,21px);font-weight:300;font-style:italic;color:var(--mist);max-width:680px;line-height:1.55;margin-bottom:32px;}
.skia-mentors .m-stats{display:flex;flex-wrap:wrap;gap:32px;}
.skia-mentors .m-stat{display:flex;flex-direction:column;}
.skia-mentors .m-stat-num{font-family:'Cormorant Display',serif;font-size:48px;font-weight:700;color:var(--gold);line-height:1;}
.skia-mentors .m-stat-lab{font-size:11px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;color:var(--stone);margin-top:6px;}

.skia-mentors .m-apply-strip{margin-top:48px;display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:24px;background:#0D1B2A;border-left:4px solid var(--gold);padding:28px 32px;position:relative;}
.skia-mentors .m-apply-strip::after{content:'';position:absolute;top:0;right:0;width:120px;height:100%;background:linear-gradient(90deg,transparent,rgba(200,168,75,.08));pointer-events:none;}
.skia-mentors .m-apply-copy{flex:1;min-width:260px;max-width:640px;position:relative;z-index:1;}
.skia-mentors .m-apply-eyebrow{font-size:10.5px;font-weight:700;letter-spacing:.30em;color:var(--gold);text-transform:uppercase;margin-bottom:8px;}
.skia-mentors .m-apply-line{font-size:15px;line-height:1.65;color:rgba(250,247,240,.92);font-weight:400;}
.skia-mentors .m-apply-line strong{color:#FAF7F0;font-weight:600;}
.skia-mentors .m-apply-actions{display:flex;flex-wrap:wrap;gap:10px;position:relative;z-index:1;}
.skia-mentors .m-apply-btn{background:var(--gold);color:#0D1B2A;border:none;padding:14px 24px;font-size:12px;font-weight:700;letter-spacing:.20em;text-transform:uppercase;cursor:pointer;transition:transform .15s ease,background .15s ease;font-family:'DM Sans',sans-serif;}
.skia-mentors .m-apply-btn:hover{background:#E0C26F;transform:translateY(-1px);}
.skia-mentors .m-apply-btn-line{background:transparent;color:var(--gold);border:1.5px solid var(--gold);padding:14px 24px;font-size:12px;font-weight:700;letter-spacing:.20em;text-transform:uppercase;cursor:pointer;transition:background .15s ease,color .15s ease;font-family:'DM Sans',sans-serif;}
.skia-mentors .m-apply-btn-line:hover{background:var(--gold);color:#0D1B2A;}
@media (max-width:680px){.skia-mentors .m-apply-strip{flex-direction:column;align-items:flex-start;padding:22px 24px;}}

.skia-mentors .m-empty{max-width:760px;margin:60px auto;padding:48px 56px;background:#FFFFFF;border:1px solid var(--line);text-align:center;}
.skia-mentors .m-empty-eyebrow{font-size:11px;font-weight:700;letter-spacing:.34em;color:var(--gold);text-transform:uppercase;}
.skia-mentors .m-empty-h2{font-family:'Cormorant Display',serif;font-size:34px;font-weight:700;color:var(--ink);line-height:1.2;margin:18px 0 0;}
.skia-mentors .m-empty-h2 em{color:var(--gold);font-style:italic;font-weight:600;}
.skia-mentors .m-empty-rule{width:48px;height:1px;background:var(--gold);margin:22px auto;}
.skia-mentors .m-empty-lead{font-size:15px;line-height:1.7;color:#3A4A5C;margin:0 auto 32px;max-width:560px;}
.skia-mentors .m-empty-meta{display:flex;gap:24px;justify-content:center;margin:0 0 36px;flex-wrap:wrap;}
.skia-mentors .m-empty-stat{padding:14px 22px;border-left:2px solid var(--gold);text-align:left;background:#FBF8EE;min-width:140px;}
.skia-mentors .m-empty-stat span{display:block;font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:var(--ink);}
.skia-mentors .m-empty-stat label{display:block;font-size:10px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#8A6A14;margin-top:4px;}
.skia-mentors .m-empty-actions{display:flex;gap:12px;justify-content:center;margin-bottom:24px;flex-wrap:wrap;}
.skia-mentors .m-empty-foot{font-size:12.5px;color:#7A8190;line-height:1.7;font-style:italic;margin:18px auto 0;max-width:520px;}
@media (max-width:680px){.skia-mentors .m-empty{padding:32px 24px;}.skia-mentors .m-empty-h2{font-size:26px;}}

.skia-mentors .m-section{max-width:1320px;margin:0 auto;padding:24px 32px 100px;}
.skia-mentors .m-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;}
@media (max-width:1024px){.skia-mentors .m-grid{grid-template-columns:repeat(2,1fr);}}
@media (max-width:640px){.skia-mentors .m-grid{grid-template-columns:1fr;}}

.skia-mentors .m-card{background:linear-gradient(170deg,var(--ink3) 0%,var(--ink2) 100%);border:1px solid rgba(13,27,42,.07);overflow:hidden;transition:all .3s;position:relative;}
.skia-mentors .m-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(to right,transparent,var(--gold),transparent);opacity:0;transition:opacity .3s;}
.skia-mentors .m-card:hover{border-color:var(--gold-line);transform:translateY(-3px);}
.skia-mentors .m-card:hover::before{opacity:1;}
.skia-mentors .m-card-head{padding:28px 28px 20px;display:flex;align-items:flex-start;gap:16px;}
.skia-mentors .m-avatar{width:64px;height:64px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:var(--ink);background:linear-gradient(135deg,var(--gold) 0%,var(--gold2) 100%);flex-shrink:0;letter-spacing:.02em;}
.skia-mentors .m-name{font-family:'DM Sans',sans-serif;font-size:18px;font-weight:700;color:var(--white);margin-bottom:4px;letter-spacing:0;}
.skia-mentors .m-expertise{font-size:11px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:var(--gold);}
.skia-mentors .m-body{padding:0 28px 28px;}
.skia-mentors .m-bio{font-size:14px;color:var(--mist);line-height:1.65;margin-bottom:18px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
.skia-mentors .m-links{display:flex;gap:8px;margin-bottom:20px;}
.skia-mentors .m-link{width:36px;height:36px;border-radius:50%;background:var(--gold-subtle);border:1px solid var(--gold-line);color:var(--gold);display:inline-flex;align-items:center;justify-content:center;text-decoration:none;transition:all .2s;}
.skia-mentors .m-link:hover{background:var(--gold);color:var(--ink);}
.skia-mentors .m-startups-head{display:flex;align-items:center;justify-content:space-between;padding-top:18px;border-top:1px solid rgba(200,168,75,.1);margin-bottom:14px;}
.skia-mentors .m-startups-head .lab{font-size:11px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;color:var(--stone);display:flex;align-items:center;gap:8px;}
.skia-mentors .m-startups-head .badge{background:var(--gold-subtle);border:1px solid var(--gold-line);color:var(--gold);font-size:11px;font-weight:700;padding:3px 10px;border-radius:50px;letter-spacing:.04em;}
.skia-mentors .m-startup{display:flex;align-items:center;justify-content:space-between;background:var(--ink);border:1px solid rgba(13,27,42,.05);padding:10px 12px;border-radius:4px;margin-bottom:6px;}
.skia-mentors .m-startup-name{font-size:13px;color:var(--cream);font-weight:500;}
.skia-mentors .m-startup-score{font-size:11px;color:var(--gold);background:var(--gold-subtle);border:1px solid var(--gold-line);padding:2px 8px;border-radius:4px;font-weight:700;letter-spacing:.04em;}
.skia-mentors .m-more{font-size:11px;color:var(--stone);text-align:center;letter-spacing:.04em;font-style:italic;}

.skia-mentors .m-loading{min-height:60vh;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:18px;}
.skia-mentors .m-spinner{width:48px;height:48px;border:3px solid var(--gold-subtle);border-top-color:var(--gold);border-radius:50%;animation:msp 1s linear infinite;}
@keyframes msp{to{transform:rotate(360deg);}}
`;

const MentorsPage = () => {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState([]);
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [mentorsRes, startupsRes] = await Promise.all([
        axios.get(`${API}/mentors`),
        axios.get(`${API}/startups`)
      ]);
      setMentors(mentorsRes.data);
      setStartups(startupsRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const getStartupsForMentor = (mentorName) => {
    return startups.filter(s =>
      s.mentor && s.mentor.toLowerCase().includes(mentorName.split(' ')[0].toLowerCase())
    );
  };

  return (
    <div className="skia-mentors">
      <style dangerouslySetInnerHTML={{ __html: MENTORS_STYLES }} />
      <Header />

      {loading ? (
        <div className="m-loading">
          <div className="m-spinner" />
          <p style={{ color: '#B8B0A4', fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', fontWeight: 600 }}>Loading mentors…</p>
        </div>
      ) : (
        <>
          <section className="m-hero">
            <div className="m-hero-grid" />
            <div className="m-hero-glow" />
            <div className="m-hero-mono">MENTORS</div>
            <div className="m-hero-inner">
              <div className="m-eyebrow">World-class operators</div>
              <h1 className="m-h1">Industry experts <em>guiding</em><br />the next 20 chosen.</h1>
              <p className="m-sub">A curated network of operators, investors and advisors backing every founder inside SKI AXIS — across governance, fundraising, scaling and exits.</p>
              <div className="m-stats">
                <div className="m-stat" data-testid="stat-mentors">
                  <span className="m-stat-num">{mentors.length === 0 ? '—' : mentors.length}</span>
                  <span className="m-stat-lab">Approved Faculty</span>
                </div>
                <div className="m-stat" data-testid="stat-startups">
                  <span className="m-stat-num">Q3 2026</span>
                  <span className="m-stat-lab">First Cohort Live</span>
                </div>
              </div>

              <div className="m-apply-strip" data-testid="mentor-apply-strip">
                <div className="m-apply-copy">
                  <div className="m-apply-eyebrow">Mentor Faculty · Open Applications</div>
                  <div className="m-apply-line">Are you a senior operator, investor or domain expert? Join the SKI Axis faculty — <strong>75% revenue share, quarterly payouts, full marketing engine.</strong></div>
                </div>
                <div className="m-apply-actions">
                  <button
                    className="m-apply-btn"
                    onClick={() => navigate('/mentor-registration')}
                    data-testid="mentor-apply-btn"
                  >Apply for Mentorship →</button>
                  <button
                    className="m-apply-btn-line"
                    onClick={() => navigate('/mentor-workshop-preview')}
                    data-testid="mentor-preview-btn"
                  >Preview the dashboard</button>
                </div>
              </div>
            </div>
          </section>

          <section className="m-section">
            {mentors.length === 0 ? (
              <div className="m-empty" data-testid="mentors-empty-state">
                <div className="m-empty-eyebrow">Faculty in Formation</div>
                <h2 className="m-empty-h2">The founding faculty is being curated <em>by invitation only.</em></h2>
                <div className="m-empty-rule" />
                <p className="m-empty-lead">
                  SKI Axis is in <strong>active outreach</strong> to a hand-picked group of governance architects, institutional capital advisors, and operating partners. The first cohort of approved mentors will appear here as their applications are reviewed and confirmed.
                </p>
                <div className="m-empty-meta">
                  <div className="m-empty-stat"><span>Q3 2026</span><label>First Mentors Live</label></div>
                  <div className="m-empty-stat"><span>75%</span><label>Revenue To Mentors</label></div>
                  <div className="m-empty-stat"><span>By Invitation</span><label>Founding Cohort</label></div>
                </div>
                <div className="m-empty-actions">
                  <button className="m-apply-btn" onClick={() => navigate('/mentor-registration')} data-testid="empty-apply-btn">Apply for the Faculty →</button>
                  <button className="m-apply-btn-line" onClick={() => navigate('/mentor-workshop-preview')} data-testid="empty-preview-btn">See the mentor experience</button>
                </div>
                <p className="m-empty-foot">
                  Are you a senior operator, investor, or domain expert who wants to teach the SKI Axis Cohort 1 founders? Apply — every application is personally reviewed by Dr. Neha Dewan.
                </p>
              </div>
            ) : (
              <div className="m-grid">
                {mentors.map((mentor, idx) => {
                const assigned = getStartupsForMentor(mentor.name);
                const initials = mentor.name.split(' ').map(n => n[0]).join('').slice(0, 2);
                return (
                  <article key={mentor.id || idx} className="m-card" data-testid={`mentor-card-${idx}`}>
                    <div className="m-card-head">
                      <div className="m-avatar" style={mentor.photo_url ? { background: `url(${mentor.photo_url}) center/cover`, color: 'transparent' } : {}}>{!mentor.photo_url && initials}</div>
                      <div style={{ flex: 1 }}>
                        <div className="m-name">{mentor.name}</div>
                        <div className="m-expertise">{mentor.expertise}</div>
                      </div>
                    </div>
                    <div className="m-body">
                      <p className="m-bio">{mentor.bio || "Expert mentor providing guidance to SKI AXIS startups."}</p>
                      <div className="m-links">
                        {mentor.linkedin_url && (
                          <a href={mentor.linkedin_url} target="_blank" rel="noopener noreferrer" className="m-link" aria-label="LinkedIn">
                            <Linkedin size={14} />
                          </a>
                        )}
                        {mentor.email && (
                          <a href={`mailto:${mentor.email}`} className="m-link" aria-label="Email">
                            <Mail size={14} />
                          </a>
                        )}
                      </div>
                      <div className="m-startups-head">
                        <span className="lab"><Building2 size={12} /> Assigned Startups</span>
                        <span className="badge">{assigned.length}</span>
                      </div>
                      <div>
                        {assigned.slice(0, 3).map((s, si) => (
                          <div key={si} className="m-startup">
                            <span className="m-startup-name">{s.name}</span>
                            <span className="m-startup-score">{s.governance_score}/10</span>
                          </div>
                        ))}
                        {assigned.length > 3 && (
                          <p className="m-more">+{assigned.length - 3} more startups</p>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
              </div>
            )}
          </section>
        </>
      )}

      <Footer />
    </div>
  );
};

export default MentorsPage;
